import { useState } from "react";
import type { Category, VatRate } from "../../api/types";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { categoryDraft, COLOR_GROUPS, deleteCategory, saveCategory, type CategoryDraft } from "./menu-edit";
import type { MenuEditor } from "./useMenuEditor";

interface Props {
  editor: MenuEditor;
  category: Category | null;
  onSaved: (id: string) => void;
  onClose: () => void;
}

const RATES: VatRate[] = [6, 12, 21];
/** The quick picks of the prototype: food 6/12, soft drinks 6/21, alcohol 21/21. */
const QUICK = [
  { key: "food", rates: [6, 12] },
  { key: "drink", rates: [6, 21] },
  { key: "alcohol", rates: [21, 21] },
] as const;

/** Nieuwe categorie / Categorie aanpassen, as in the prototype. */
export function CategoryDialog({ editor, category, onSaved, onClose }: Props) {
  const { menu, toast } = editor;
  const [draft, setDraft] = useState<CategoryDraft>(() => categoryDraft(menu, category));
  const set = (change: Partial<CategoryDraft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };
  const empty =
    category !== null && !Object.values(menu.products).some((product) => product.categoryId === category.id);

  const save = async () => {
    if (draft.name.trim() === "") {
      toast(t.menuAdmin.category.needName, "bad");
      return;
    }
    const next = saveCategory(menu, category, draft);
    const id = category?.id ?? next.categories.at(-1)!.id;
    const change = category ? `Categorie ${draft.name.trim()} aangepast` : `Categorie ${draft.name.trim()} toegevoegd`;
    if (await editor.commit(next, change)) {
      toast(category ? t.menuAdmin.category.saved : t.menuAdmin.category.added(draft.name.trim()));
      onSaved(id);
      onClose();
    }
  };

  const remove = async () => {
    if (!category) return;
    if (await editor.commit(deleteCategory(menu, category.id), `Categorie ${category.name} verwijderd`)) {
      toast(t.menuAdmin.category.deleted);
      onSaved(menu.categories.find((item) => item.id !== category.id)?.id ?? "");
      onClose();
    }
  };

  const text = t.menuAdmin.category;
  return (
    <Dialog
      title={category ? text.edit : text.new}
      onClose={onClose}
      footer={
        <>
          {empty && (
            <button type="button" className="btn danger" disabled={editor.busy} onClick={() => void remove()}>
              {t.menuAdmin.product.delete}
            </button>
          )}
          {category && !empty && <span className="note">{text.deleteOnlyEmpty}</span>}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t.menuAdmin.cancel}
          </button>
          <button type="button" className="btn primary" disabled={editor.busy} onClick={() => void save()}>
            {t.menuAdmin.product.save}
          </button>
        </>
      }
    >
      <label className="field">
        {text.name}
        <input
          value={draft.name}
          placeholder={text.namePlaceholder}
          onChange={(event) => {
            set({ name: event.target.value });
          }}
          data-autofocus
        />
      </label>
      <div>
        <span className="flabel">{text.color}</span>
        {/* two groups to choose from: the strong colours and the quiet ones (Furkan, 03/10/2026) */}
        {COLOR_GROUPS.map((group) => (
          <div key={group.key} className="cgroup" role="group" aria-label={text.colorGroups[group.key]}>
            <span className="cgname">{text.colorGroups[group.key]}</span>
            <div className="colors">
              {group.colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  className="csw"
                  style={{ background: color }}
                  aria-pressed={draft.color.toUpperCase() === color}
                  aria-label={text.colorLabel(color)}
                  onClick={() => {
                    set({ color });
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="pesec">
        <b>{text.sizesTitle}</b>
        <div className="pesh">
          <span>
            {text.web} <span className="note">{text.webHint(draft.name)}</span>
          </span>
          <YesNo
            value={draft.webSplitBySize}
            label={text.web}
            onChange={(webSplitBySize) => {
              set({ webSplitBySize });
            }}
          />
        </div>
        <div className="pesh">
          <span>
            {text.till} <span className="note">{text.tillHint}</span>
          </span>
          <YesNo
            value={draft.tillSplitBySize}
            label={text.till}
            onChange={(tillSplitBySize) => {
              set({ tillSplitBySize });
            }}
          />
        </div>
        <p className="note">{text.sizesNote}</p>
      </div>
      <div className="pesec">
        <b>{text.vatTitle}</b>
        <div className="pesh">
          <span>{text.vatTakeaway}</span>
          <div className="seg" role="group" aria-label={text.vatTakeaway}>
            {RATES.map((rate) => (
              <button
                key={rate}
                type="button"
                aria-pressed={draft.vatTakeaway === rate}
                onClick={() => {
                  set({ vatTakeaway: rate });
                }}
              >
                {rate}%
              </button>
            ))}
          </div>
        </div>
        <div className="pesh">
          <span>{text.vatOnSite}</span>
          <div className="seg" role="group" aria-label={text.vatOnSite}>
            {RATES.map((rate) => (
              <button
                key={rate}
                type="button"
                aria-pressed={draft.vatOnSite === rate}
                onClick={() => {
                  set({ vatOnSite: rate });
                }}
              >
                {rate}%
              </button>
            ))}
          </div>
        </div>
        <div className="pesh">
          <span className="note">{text.quick}</span>
          <div className="vpick">
            {QUICK.map(({ key, rates }) => (
              <button
                key={key}
                type="button"
                className="btn"
                aria-pressed={draft.vatTakeaway === rates[0] && draft.vatOnSite === rates[1]}
                onClick={() => {
                  set({ vatTakeaway: rates[0], vatOnSite: rates[1] });
                }}
              >
                {text[key]}
                <small>
                  {rates[0]}% / {rates[1]}%
                </small>
              </button>
            ))}
          </div>
        </div>
        <p className="note">{text.vatNote}</p>
      </div>
    </Dialog>
  );
}

/** The Ja / Nee switch of the prototype. */
export function YesNo({
  value,
  label,
  onChange,
}: {
  value: boolean;
  label: string;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      <button
        type="button"
        aria-pressed={value}
        onClick={() => {
          onChange(true);
        }}
      >
        {t.menuAdmin.product.yes}
      </button>
      <button
        type="button"
        aria-pressed={!value}
        onClick={() => {
          onChange(false);
        }}
      >
        {t.menuAdmin.product.no}
      </button>
    </div>
  );
}
